import {FormikErrors, FormikValues} from "formik";
import {FormFieldType} from "enum/FormFieldType";
import Utils from "platform/util/Utils";
import {TradeType} from "protocol/trade/TradeType";
import {Ranges} from "protocol/trade/Ranges";
import {PositionDirection} from "platform/protocol/enum/PositionDirection";
import Translations from "platform/translation/Translations";
import { TranslationKey } from "enum/TranslationKey";
import {isValidNumber} from "libphonenumber-js";
import {DealForm} from "core/redux/deal/DealReduxState";

export class Validation {

    public static validateDeal(fields: FormFieldType[], form: DealForm, ranges: Ranges): {[key: string]: any} {
        const errors: {[key: string]: any} = {};
        if (fields) {
            fields.forEach((fieldType: FormFieldType) => {
                switch (fieldType) {
                    case FormFieldType.Amount:
                        const amount: number = form[fieldType];
                        if (!Utils.greaterThen0(amount)) {
                            errors[fieldType] = "Required";
                        } else {
                            const minAmount: number = form[FormFieldType.MinAmount];
                            if (minAmount > amount) {
                                errors[fieldType] = "Amount lower then min amount: " + minAmount;
                            }
                        }
                        break;
                    case FormFieldType.SymbolId:
                    case FormFieldType.PositionDirection:
                        if (!Utils.greaterThen0(form[fieldType])) {
                            errors[fieldType] = "Required";
                        }
                        break;
                    case FormFieldType.RateRequestType:
                        if (!Utils.greaterThen0(form[fieldType])) {
                            errors[fieldType] = "Required";
                        } else {
                            Validation.validateOrderRate(fields, form, ranges, errors);
                        }
                        break;
                    case FormFieldType.OrderRate:
                        Validation.validateOrderRate(fields, form, ranges, errors);
                        break;
                    case FormFieldType.StopLossChecked:
                    case FormFieldType.StopLossValueRate:
                    case FormFieldType.StopLossValueAmount:
                        const  {StopLossValueRate, StopLossMinimum, StopLossMaximum} = ranges;
                        if (form[FormFieldType.StopLossChecked] && (Utils.isNull(StopLossValueRate) || StopLossValueRate < StopLossMinimum || StopLossValueRate > StopLossMaximum)) {
                            errors[FormFieldType.StopLossValueRate] = "Stop loss rate out of range";
                        }
                        break;
                    case FormFieldType.TakeProfitChecked:
                    case FormFieldType.TakeProfitValueRate:
                    case FormFieldType.TakeProfitValueAmount:
                        const  {TakeProfitValueRate, TakeProfitMinimum, TakeProfitMaximum} = ranges;
                        if (form[FormFieldType.TakeProfitChecked] && (Utils.isNull(TakeProfitValueRate) || TakeProfitValueRate < TakeProfitMinimum || TakeProfitValueRate > TakeProfitMaximum)) {
                            errors[FormFieldType.TakeProfitValueRate] = "Take profit rate out of range";
                        }
                        break;
                    case FormFieldType.ExpirationChecked:
                    case FormFieldType.ExpirationDay:
                    case FormFieldType.ExpirationMonth:
                    case FormFieldType.ExpirationYear:
                        if (form[FormFieldType.ExpirationChecked]) {
                            const day: number = form[FormFieldType.ExpirationDay];
                            const month: number = form[FormFieldType.ExpirationMonth];
                            const year: number = form[FormFieldType.ExpirationYear];
                            if (!Utils.greaterThen0(day)) {
                                errors[FormFieldType.ExpirationDay] = "Required";
                            } else if (!Utils.greaterThen0(month)) {
                                errors[FormFieldType.ExpirationMonth] = "Required";
                            } else if (!Utils.greaterThen0(year)) {
                                errors[FormFieldType.ExpirationYear] = "Required";
                            }
                        }
                        break;
                }
            });
        }
        return errors;
    }

    private static validateOrderRate<Form>(fields: FormFieldType[], form: DealForm, ranges: Ranges, errors: {[key: string]: any}): void {
        const tradeType: TradeType = form[FormFieldType.RateRequestType];
        if (tradeType === TradeType.EntryOrder) {
            const orderRate: number = form[FormFieldType.OrderRate];
            const direction: PositionDirection = form[FormFieldType.PositionDirection];
            const {EntryLimitMinimum, EntryStopMaximum, EntryStopMinimum, EntryLimitMaximum} = ranges;
            const minimum: number = direction === PositionDirection.Sell ? EntryStopMaximum : EntryLimitMaximum;
            const maximum: number = direction === PositionDirection.Sell ?  EntryLimitMinimum : EntryStopMinimum;
            if (!Utils.greaterThen0(orderRate) || (orderRate < maximum && orderRate > minimum)) {
                errors[FormFieldType.OrderRate] = "Order rate out of range";
            }
        }
    }

    public static validateWithdrawal<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const amount: number = values[FormFieldType.Amount];
        if (!Utils.greaterThen0(amount)) {
            errors[FormFieldType.Amount] = "Invalid";
        }
        if (Utils.isEmpty(values[FormFieldType.Reason])) {
            errors[FormFieldType.Reason] = "Required";
        }
        return errors;
    }

    public static validatePassword<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const currentPassword: string = values[FormFieldType.CurrentPassword];
        const newPassword: string = values[FormFieldType.NewPassword];
        const confirmPassword: string = values[FormFieldType.ConfirmPassword];

        if (Utils.isEmpty(currentPassword)) {
            errors[FormFieldType.CurrentPassword] = Translations.text(TranslationKey.errorModalRequired);
        }
        if (Utils.isEmpty(newPassword)) {
            errors[FormFieldType.NewPassword] = Translations.text(TranslationKey.errorModalRequired);
        }
        if (Utils.isEmpty(confirmPassword)) {
            errors[FormFieldType.ConfirmPassword] = Translations.text(TranslationKey.errorModalRequired);
        }
        if (newPassword !== confirmPassword) {
            errors[FormFieldType.ConfirmPassword] = Translations.text(TranslationKey.errorPasswordsNotMatch);
        }
        return errors;
    }

    public static validateCloseOutMethod<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const closeOutMethod: string = values[FormFieldType.CloseOutMethod];

        if (Utils.isEmpty(closeOutMethod)) {
            errors[FormFieldType.CloseOutMethod] = Translations.text(TranslationKey.errorModalRequired);
        }

       return errors;
    }

    public static validateMyLeverage<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const value: string = values[FormFieldType.MyLeverageValue];

        if (Utils.isEmpty(value)) {
            errors[FormFieldType.MyLeverageValue] = Translations.text(TranslationKey.errorModalRequired);
        }

        return errors;
    }

    public static validateAnnualStatement<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const value: string = values[FormFieldType.AnnualStatementYear];

        if (Utils.isEmpty(value)) {
            errors[FormFieldType.AnnualStatementYear] = Translations.text(TranslationKey.errorModalRequired);
        }

        return errors;
    }

    public static validateNewEmail<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const value: string = values[FormFieldType.Email];
        const regExp: RegExp = new RegExp("(?!.*?[._%+-]{2,})(?=[a-zA-Z0-9@._%+-]{6,254}$)(^[a-zA-Z0-9])[a-zA-Z0-9._%+-]{1,64}([^._%+-]{1,})@(?:[a-zA-Z0-9-]{1,63}\\.){1,8}[a-zA-Z]{2,63}");

        if (Utils.isEmpty(value)) {
            errors[FormFieldType.Email] = Translations.text(TranslationKey.errorModalRequired);
        }

        if (!regExp.test(value)) {
            errors[FormFieldType.Email] = Translations.text(TranslationKey.settingsProfileInvalidEmail);
        }

        return errors;
    }

    public static validatePhoneNumber<Form>(values: FormikValues): FormikErrors<Form> {
        const errors: FormikErrors<Form> = {};
        const phoneNumber: string = values[FormFieldType.PhoneNumber];
        const countryCode: string = values[FormFieldType.PnoneNumberCountryCode]

        if (Utils.isEmpty(phoneNumber)) {
            errors[FormFieldType.PhoneNumber] = Translations.text(TranslationKey.errorModalRequired);
        }

        if (Utils.isEmpty(countryCode)) {
            errors[FormFieldType.PnoneNumberCountryCode] = Translations.text(TranslationKey.errorModalRequired);
        }

        if (!isValidNumber(`${countryCode}${phoneNumber}`)) {
            errors[FormFieldType.PhoneNumber] = Translations.text(TranslationKey.settingsProfileInvalidPhoneNumber);
        }

        return errors;
    }
}
